import {
  Flex,
  Text,
  Heading,
  useBreakpointValue,
  Tooltip,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { PieChart, Pie } from 'recharts';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { TAccountTotalAmountsGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from '~/packages/common-frontend/colors';
import { formatCurrencyAmount } from '~/packages/common-frontend/utils';
import { TransactionsFilterState } from '~/packages/transactions/hooks/transactionsHooks';
import { useGetDailyAccountActivityQuery } from '~/packages/transactions/requests/transactions.gql.generated';

// types
type ChartDataSlice = {
  name: string;
  value: number;
  fill: string;
};

type PieChartProps = {
  data: ChartDataSlice[];
};

// constants
const COLORS = Object.values(colors.chart);
const MAX_VISIBLE_TRANSACTIONS = 9;
const CIRCLE_SIZE = '.625rem';
const CIRCLE_PROPS = {
  borderRadius: '50%',
  marginRight: '0.5rem',
  height: CIRCLE_SIZE,
  width: CIRCLE_SIZE,
};

// fragments
const PieChartFragment = ({ data }: PieChartProps) => {
  const chartWidth = useBreakpointValue({ base: 200, lg: 240 });
  const chartHeight = useBreakpointValue({ base: 200, lg: 240 });
  const chartInnerRadius = useBreakpointValue({ base: 60, lg: 80 });
  const chartOuterRadius = useBreakpointValue({ base: 100, lg: 110 });

  const chartData =
    data.length === 0
      ? [
          {
            name: 'No data',
            value: 1,
            fill: colors.gray[200],
          },
        ]
      : data;

  return (
    <PieChart height={chartHeight} width={chartWidth}>
      <Pie
        cx="50%"
        cy="50%"
        data={chartData}
        dataKey="value"
        innerRadius={chartInnerRadius}
        outerRadius={chartOuterRadius}
        style={{ outline: 'none' }} // disable blue outline when Piechart clicked
      />
    </PieChart>
  );
};

// fragments
const TAccountCell = ({
  accountTotal,
  circleColor,
  flexParentStyles,
  titleStyles,
}: {
  accountTotal: { tAccount: string; totalAmount: number };
  circleColor: string;
  flexParentStyles?: React.CSSProperties;
  titleStyles?: React.CSSProperties;
}) => (
  <Flex
    sx={{
      color: colors.black[70],
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '.125rem',
      flexWrap: 'nowrap',
      gap: '10',
      ...flexParentStyles,
    }}
  >
    <Flex align="center">
      <span
        style={{
          backgroundColor: circleColor,
          ...CIRCLE_PROPS,
          flexShrink: 0,
        }}
      />
      <Text fontSize="sm" sx={{ ...titleStyles }}>
        {commonUtils.toTitleCase(accountTotal.tAccount)}
      </Text>
    </Flex>
    <Text fontSize="sm">
      {formatCurrencyAmount(accountTotal.totalAmount, Bank.Currency.USD)}
    </Text>
  </Flex>
);

export const TransactionsChartSummary = ({
  creditFacilityIds,
  loanIds,
  accountTypes,
  startDate,
  endDate,
}: TransactionsFilterState) => {
  const dailyAccountActivityQuery = useGetDailyAccountActivityQuery({
    fetchPolicy: 'no-cache',
    variables: {
      creditFacilityIds,
      loanIds,
      accountTypes,
      startDate,
      endDate,
    },
  });

  const dailyAccountActivity = dailyAccountActivityQuery.data?.activity;

  const columns = useBreakpointValue({ base: 1, lg: 1, xl: 2 });
  const {
    chartData,
    otherAccountsTotals,
    otherAccountsTotalsSum,
    visibleAccountsTotals,
  } = useMemo(() => {
    let allAccountTotalsZero = true;
    const visibleAccounts: TAccountTotalAmountsGQLType[] = [];
    const otherAccounts: TAccountTotalAmountsGQLType[] = [];
    let otherAccountsSum = 0;
    const visibleChartData = [];

    if (dailyAccountActivity?.metadata) {
      dailyAccountActivity.metadata.accountTotals
        .filter(
          (accountTotal) =>
            accountTotal.tAccount !== 'longTermDebt' &&
            accountTotal.totalAmount >= 0,
        )
        .forEach((accountTotal, idx) => {
          if (accountTotal.totalAmount !== 0) {
            allAccountTotalsZero = false;
          }
          if (idx < MAX_VISIBLE_TRANSACTIONS) {
            visibleAccounts.push(accountTotal);
            visibleChartData.push({
              name: accountTotal.tAccount.toString(),
              value: accountTotal.totalAmount,
              fill: COLORS[idx],
            });
          } else {
            otherAccounts.push(accountTotal);
            otherAccountsSum += accountTotal.totalAmount;
          }
        });
      visibleChartData.push({
        name: 'other accounts',
        value: otherAccountsSum,
        fill: COLORS[MAX_VISIBLE_TRANSACTIONS],
      });
    }

    return {
      chartData: allAccountTotalsZero
        ? [
            {
              name: 'All Account Total Amounts Are Zero',
              value: 1,
              fill: colors.gray[100],
            },
          ]
        : visibleChartData,
      otherAccountsTotals: otherAccounts,
      otherAccountsTotalsSum: otherAccountsSum,
      visibleAccountsTotals: visibleAccounts,
    };
  }, [dailyAccountActivity]);

  if (!dailyAccountActivity || !dailyAccountActivity.metadata) return <></>;

  return (
    <Flex
      alignItems="center"
      backgroundColor={colors.background[50]}
      borderColor={colors.gray[100]}
      borderRadius="md"
      borderWidth="thin"
      justifyContent="center"
      overflow="hidden"
      wrap="wrap"
    >
      <Flex p="3rem">
        <PieChartFragment data={chartData} />
      </Flex>
      <Flex direction="column" flex="1" justify="center" p="2rem">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mb="1rem"
          wrap="wrap"
        >
          <Heading as="h3" color={colors.black[95]} fontSize="1.25rem">
            Total Owed
          </Heading>
          <Heading as="h3" fontSize="1.25rem">
            {formatCurrencyAmount(
              dailyAccountActivity.metadata.totalCurrentLiabilities || 0,
              Bank.Currency.USD,
            )}
          </Heading>
        </Flex>
        <Flex columnGap={columns === 2 ? '1rem' : '0'} flexFlow="wrap">
          {visibleAccountsTotals.map((accountTotal, idx) => (
            <TAccountCell
              key={accountTotal.tAccount}
              accountTotal={accountTotal}
              circleColor={COLORS[idx]}
              flexParentStyles={{
                width: columns === 2 ? 'calc(50% - 0.5rem)' : '100%',
              }}
            />
          ))}
          {otherAccountsTotals.length > 0 && (
            <Tooltip
              label={
                <Flex direction="column" padding=".5rem">
                  {otherAccountsTotals.map((accountTotal) => (
                    <TAccountCell
                      key={accountTotal.tAccount}
                      accountTotal={accountTotal}
                      circleColor={COLORS[MAX_VISIBLE_TRANSACTIONS]}
                      flexParentStyles={{
                        color: 'white',
                      }}
                    />
                  ))}
                </Flex>
              }
            >
              <Flex
                sx={{
                  color: colors.black[70],
                  width: columns === 2 ? 'calc(50% - 0.5rem)' : '100%',
                }}
              >
                <TAccountCell
                  accountTotal={{
                    tAccount: 'Other Accounts',
                    totalAmount: otherAccountsTotalsSum,
                  }}
                  circleColor={COLORS[MAX_VISIBLE_TRANSACTIONS]}
                  titleStyles={{
                    borderBottom: '1px dotted gray',
                    cursor: 'info',
                  }}
                  flexParentStyles={{ width: '100%' }}
                />
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
